package com.veldekar.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.veldekar.toSitePalette
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.Canvas
import kotlin.random.Random

private val pendingChartJsCallbacks = mutableListOf<() -> Unit>()
private var chartJsLoading = false

private fun loadChartJsAndRender(renderChart: () -> Unit) {
    val isLoaded = js(
        // language=javascript
        "typeof window.Chart !== 'undefined' && typeof window.Chart.registry !== 'undefined' && window.Chart.registry.plugins.get('annotation') != null"
    ) as Boolean
    if (isLoaded) {
        window.setTimeout(renderChart, 50)
        return
    }

    pendingChartJsCallbacks.add(renderChart)

    if (chartJsLoading) return
    chartJsLoading = true

    val script = document.createElement("script")
    script.setAttribute("src", "https://cdn.jsdelivr.net/npm/chart.js")
    script.addEventListener("load", {
        val pluginScript = document.createElement("script")
        pluginScript.setAttribute("src", "https://cdn.jsdelivr.net/npm/chartjs-plugin-annotation")
        pluginScript.addEventListener("load", {
            val event = org.w3c.dom.events.Event("chartJsLoaded")
            window.dispatchEvent(event)
        })
        document.head?.appendChild(pluginScript)
    })
    document.head?.appendChild(script)

    window.addEventListener("chartJsLoaded", {
        pendingChartJsCallbacks.forEach { callback ->
            window.setTimeout(callback, 50)
        }
        pendingChartJsCallbacks.clear()
    })
}

@Composable
fun ChartJs(
    configJson: String,
    modifier: Modifier = Modifier
) {
    val uniqueId = remember { "chartjs-${Random.nextInt(100000)}" }
    val colorMode = ColorMode.current

    LaunchedEffect(configJson, colorMode) {
        val renderChart: () -> Unit = renderChart@{
            val canvas = document.getElementById(uniqueId)
            if (canvas == null) return@renderChart
            
            // We set default font color based on theme
            val palette = colorMode.toPalette()
            val sitePalette = colorMode.toSitePalette()
            val textColor = palette.color
            val gridColor = sitePalette.cobweb
            
            // language=javascript
            val js = """
                (function() {
                    Chart.defaults.color = '$textColor';
                    Chart.defaults.borderColor = '$gridColor';
                    if (Chart.defaults.scale && Chart.defaults.scale.grid) {
                        Chart.defaults.scale.grid.color = '$gridColor';
                    }
                    
                    const ctx = document.getElementById('$uniqueId');
                    // Destroy existing chart if it exists on this canvas
                    if (window.charts && window.charts['$uniqueId']) {
                        window.charts['$uniqueId'].destroy();
                    }
                    if (!window.charts) window.charts = {};
                    
                    const config = $configJson;
                    window.charts['$uniqueId'] = new Chart(ctx, config);
                })();
            """.trimIndent()
            
            val script = document.createElement("script")
            script.textContent = js
            document.body?.appendChild(script)
            script.remove()
        }

        loadChartJsAndRender(renderChart)
    }

    Box(
        modifier = modifier.fillMaxWidth(),
        contentAlignment = Alignment.Center
    ) {
        Canvas(
            attrs = {
                id(uniqueId)
            }
        )
    }
}
